import './home.css';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import api from '../api';

export default function APNClipsSubmit() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const baseUrl = useMemo(() => (import.meta.env.VITE_API_URL || 'http://localhost:5000'), []);
  const [mode, setMode] = useState('link'); // 'link' | 'upload'
  const [creatorName, setCreatorName] = useState('');
  const [platform, setPlatform] = useState('twitch');
  const [category, setCategory] = useState('funny');
  const [rightsConfirmed, setRightsConfirmed] = useState(false);

  // Link mode
  const [url, setUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [thumbUrl, setThumbUrl] = useState('');
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState('');

  // Upload mode
  const [uploadTitle, setUploadTitle] = useState('');
  const [file, setFile] = useState(null);

  // Submission processing
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submissionId, setSubmissionId] = useState(null);
  const [status, setStatus] = useState(null); // {status, storedUrl, errorMessage, title, thumbnailUrl}
  const pollTimerRef = useRef(null);
  const didPrefillRef = useRef(false);

  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: 16,
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(0,0,0,0.15)',
    color: '#fff',
    outline: 'none',
  };

  const labelStyle = { fontSize: 12, color: '#D4D8DF', opacity: 0.9, marginBottom: 6 };

  const platformOptions = [
    { value: 'twitch', label: 'Twitch' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'kick', label: 'Kick' },
    { value: 'sonstiges', label: 'Sonstiges' },
    { value: 'offline', label: 'Offline' },
  ];

  const categoryOptions = [
    { value: 'fail', label: 'Fail' },
    { value: 'win', label: 'Win' },
    { value: 'funny', label: 'Funny' },
    { value: 'wtf', label: 'WTF' },
    { value: 'skill', label: 'Skill' },
    { value: 'anderes', label: 'Anderes' },
  ];

  useEffect(() => {
    if (didPrefillRef.current) return;
    if (!user) return;
    const fallback = (user?.displayName || user?.username || '').trim();
    if (fallback) {
      setCreatorName(fallback);
      didPrefillRef.current = true;
    }
  }, [user]);

  // Fetch metadata for link submissions (title + thumbnail) with debounce
  useEffect(() => {
    if (mode !== 'link') return;
    const u = String(url || '').trim();
    setMetaError('');
    if (!u || u.length < 8) {
      setMetaLoading(false);
      setThumbUrl('');
      return;
    }

    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setMetaLoading(true);
        const res = await api.get('/apn-clips/metadata', { params: { url: u } });
        if (cancelled) return;
        const title = typeof res.data?.title === 'string' ? res.data.title : '';
        const thumb = typeof res.data?.thumbnailUrl === 'string' ? res.data.thumbnailUrl : '';
        setThumbUrl(thumb);
        // only auto-fill if user hasn't typed their own title yet
        setLinkTitle((prev) => (prev && prev.trim().length > 0 ? prev : title));
      } catch (e) {
        if (cancelled) return;
        const err = e?.response?.data;
        if (typeof err?.detail === 'string' && err.detail.trim()) {
          setMetaError(`${err?.error || 'Konnte Metadaten nicht laden'}: ${err.detail}`);
        } else {
          setMetaError(err?.error || 'Metadaten konnten nicht geladen werden.');
        }
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [mode, url]);

  async function pollSubmission(id) {
    try {
      const res = await api.get(`/apn-clips/submissions/${id}`);
      const s = res.data?.submission;
      setStatus(s);
      if (s?.status === 'ready' || s?.status === 'failed') {
        setSubmitting(false);
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      }
    } catch (e) {
      // Keep polling; transient errors shouldn't break the UX.
    }
  }

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const resetAll = () => {
    setMode('link');
    setPlatform('twitch');
    setCategory('funny');
    setRightsConfirmed(false);
    setUrl('');
    setLinkTitle('');
    setThumbUrl('');
    setMetaError('');
    setMetaLoading(false);
    setUploadTitle('');
    setFile(null);
    setSubmitting(false);
    setSubmitError('');
    setSubmissionId(null);
    setStatus(null);
  };

  const videoSrc = useMemo(() => {
    const p = String(status?.storedUrl || '').trim();
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    const base = String(baseUrl || '').replace(/\/$/, '');
    const rel = p.startsWith('/') ? p : `/${p}`;
    return base + rel;
  }, [status?.storedUrl, baseUrl]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError('');

    const cn = String(creatorName || '').trim();
    if (!cn) return setSubmitError('Bitte gib einen Creator Namen an.');
    if (!rightsConfirmed) return setSubmitError('Bitte bestätige die Rechte zur Nutzung des Materials.');

    try {
      setSubmitting(true);
      setSubmissionId(null);
      setStatus(null);

      if (mode === 'link') {
        const u = String(url || '').trim();
        if (!u) throw new Error('Bitte gib eine URL an.');
        const payload = {
          creatorName: cn,
          platform,
          category,
          rightsConfirmed: true,
          url: u,
          title: String(linkTitle || '').trim(),
        };
        const res = await api.post('/apn-clips/submissions/link', payload);
        const id = res.data?.id;
        if (!id) throw new Error('Unerwartete Server-Antwort.');
        setSubmissionId(id);
        await pollSubmission(id);
        pollTimerRef.current = setInterval(() => pollSubmission(id), 1400);
        setSubmitting(false);
        return;
      }

      // upload mode
      if (!file) throw new Error('Bitte wähle eine Video-Datei aus.');
      const title = String(uploadTitle || '').trim();
      if (!title) throw new Error('Bitte gib einen Titel an.');

      const fd = new FormData();
      fd.append('creatorName', cn);
      fd.append('platform', platform);
      fd.append('category', category);
      fd.append('rightsConfirmed', 'true');
      fd.append('title', title);
      fd.append('file', file);

      const res = await api.post('/apn-clips/submissions/upload', fd);
      const id = res.data?.id;
      if (!id) throw new Error('Unerwartete Server-Antwort.');
      setSubmissionId(id);
      await pollSubmission(id);
      pollTimerRef.current = setInterval(() => pollSubmission(id), 1400);
      setSubmitting(false);
    } catch (e) {
      const statusCode = e?.response?.status;
      if (statusCode === 413) {
        setSubmitError('Upload zu groß (Server/Proxy-Limit). Bitte kontaktiere einen Admin: z. B. bei Nginx `client_max_body_size` erhöhen.');
      } else {
        setSubmitError(e?.response?.data?.error || e?.message || 'Einreichen fehlgeschlagen.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Clip einreichen</h2>
        <p className="hero-desc" style={{ margin: '0 auto 18px', textAlign: 'center', maxWidth: 920 }}>
          Reiche Clips oder anderes Videomaterial ein.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', margin: '6px 0 14px' }}>
          <Link className="hero-btn-secondary" to="/apn-clips" style={{ textDecoration: 'none' }}>Zurück</Link>
        </div>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        <div className="highlight-card" style={{ ...card, alignItems: 'stretch' }}>
          <h2 className="highlight-title" style={{ margin: '8px 8px 12px' }}>Einreichung</h2>

          {(submissionId && status?.status === 'ready') ? (
            <div style={{ padding: '0 8px 8px' }}>
              <div style={{ fontWeight: 800, marginBottom: 10 }}>Fertig!</div>
              {videoSrc ? (
                <video controls src={videoSrc} style={{ width: '100%', maxWidth: 860, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)' }} />
              ) : (
                <div style={{ opacity: 0.85, fontSize: 14 }}>Video ist bereit, aber keine URL vorhanden.</div>
              )}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                <button type="button" className="hero-btn-secondary" onClick={() => navigate('/apn-clips')}>Fertig</button>
                <button type="button" className="hero-btn-primary" onClick={resetAll}>Weiteren Beitrag einreichen</button>
              </div>
            </div>
          ) : (submissionId && status?.status === 'failed') ? (
            <div style={{ padding: '0 8px 8px' }}>
              <div style={{ color: '#ffdddd', fontSize: 14, marginBottom: 10 }}>
                {status?.errorMessage || 'Verarbeitung fehlgeschlagen.'}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" className="hero-btn-secondary" onClick={() => navigate('/apn-clips')}>Fertig</button>
                <button type="button" className="hero-btn-primary" onClick={resetAll}>Weiteren Beitrag einreichen</button>
              </div>
            </div>
          ) : submissionId ? (
            <div style={{ padding: '0 8px 8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div
                  aria-label="Processing"
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '3px solid rgba(255,255,255,0.25)',
                    borderTopColor: '#fff',
                    animation: 'apnspin 0.9s linear infinite',
                  }}
                />
                <div style={{ fontWeight: 800 }}>Deine Einreichung wird verarbeitet…</div>
              </div>
              <div style={{ opacity: 0.85, fontSize: 14, lineHeight: 1.55 }}>
                Bitte kurz warten – der Server lädt/verarbeitet das Material.
              </div>
              <style>{`@keyframes apnspin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ display: 'grid', gap: 12, padding: '0 8px 8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                <label>
                  <div style={labelStyle}>Creator Name</div>
                  <input style={inputStyle} value={creatorName} onChange={(e) => setCreatorName(e.target.value)} placeholder="Creator" />
                </label>

                <label>
                  <div style={labelStyle}>Ursprungsplattform</div>
                  <select style={inputStyle} value={platform} onChange={(e) => setPlatform(e.target.value)}>
                    {platformOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label>
                <div style={labelStyle}>Kategorie</div>
                <select style={inputStyle} value={category} onChange={(e) => setCategory(e.target.value)}>
                  {categoryOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input type="checkbox" checked={rightsConfirmed} onChange={(e) => setRightsConfirmed(e.target.checked)} />
                <span style={{ fontSize: 13, color: '#D4D8DF', lineHeight: 1.4 }}>
                  Ich bestätige, dass ich die Rechte zur Nutzung dieses Clips/Materials besitze und dem AP‑Network die Veröffentlichung für Marketing‑ und Promotionzwecke erlaube.
                </span>
              </label>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={mode === 'link' ? 'hero-btn-primary' : 'hero-btn-secondary'}
                  onClick={() => setMode('link')}
                >
                  Option A – Link einreichen
                </button>
                <button
                  type="button"
                  className={mode === 'upload' ? 'hero-btn-primary' : 'hero-btn-secondary'}
                  onClick={() => setMode('upload')}
                >
                  Option B – Datei Upload
                </button>
              </div>

              {mode === 'link' ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  <label>
                    <div style={labelStyle}>URL zum Clip/Beitrag</div>
                    <input style={inputStyle} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
                  </label>

                  <label>
                    <div style={labelStyle}>Titel (automatisch erkannt, anpassbar)</div>
                    <input style={inputStyle} value={linkTitle} onChange={(e) => setLinkTitle(e.target.value)} placeholder="Titel" />
                  </label>

                  {metaLoading && (
                    <div style={{ fontSize: 13, opacity: 0.8 }}>Lade Metadaten…</div>
                  )}
                  {metaError && (
                    <div style={{ fontSize: 13, color: '#ffdddd' }}>{metaError}</div>
                  )}
                  {thumbUrl && (
                    <div>
                      <div style={{ fontSize: 12, color: '#D4D8DF', opacity: 0.9, marginBottom: 6 }}>Thumbnail (wenn verfügbar)</div>
                      <img alt="Thumbnail" src={thumbUrl} style={{ width: '100%', maxWidth: 520, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)' }} />
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  <label>
                    <div style={labelStyle}>Titel</div>
                    <input style={inputStyle} value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} placeholder="Titel" />
                  </label>
                  <label>
                    <div style={labelStyle}>Video-Datei (max. 2 Minuten)</div>
                    <input
                      style={{ ...inputStyle, padding: 8 }}
                      type="file"
                      accept="video/*"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    Hinweis: Es werden nur Video-Dateien akzeptiert; die Abspieldauer darf nicht länger als 2 Minuten sein.
                  </div>
                </div>
              )}

              {submitError && (
                <div style={{ color: '#ffdddd', fontSize: 13 }}>{submitError}</div>
              )}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button disabled={submitting} type="submit" className="hero-btn-primary">Absenden</button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
